import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { convertTreeToOrder, duplicateTree, flattenTree } from '../../utils/driver-tree';
import { NonFinancialDriverTree, NonFinancialDriverTreeNode } from '../../types';
import { DriverDragHandlers, useDriversDragDrop } from '../../hooks/useDriversDragDrop/useDriversDragDrop';
import { useDriversView } from './drivers-view-context';

interface DriverReorderContextValue {
  isReordering: boolean;
  isSaving: boolean;
  draftTree: NonFinancialDriverTree | undefined;
  flatData: NonFinancialDriverTreeNode[];
  drag: DriverDragHandlers;
  sourceTree: NonFinancialDriverTree;
  startReordering: () => void;
  cancelReordering: () => void;
  saveReordering: () => Promise<void>;
}

const DriverReorderContext = createContext<DriverReorderContextValue>(({} as unknown) as DriverReorderContextValue);

interface Props {
  children: ReactNode;
  tree: NonFinancialDriverTree;
  isUpdating: boolean;
}

export const DriverReorderProvider = ({ children, tree, isUpdating }: Props) => {
  const { updateDriverOrdering } = useDriversView();

  const [draftTree, setDraftTree] = useState<NonFinancialDriverTree | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const isReordering = draftTree !== undefined;

  // When tree prop changes after a save, clear the draft (fresh data arrived)
  useEffect(() => {
    if (isSaving) {
      setIsSaving(false);
      setDraftTree(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  const flatData = useMemo(() => {
    if (draftTree) {
      return flattenTree(draftTree.tree);
    }
    return flattenTree(tree.tree);
  }, [draftTree, tree]);

  const handleDragAndDropUpdate = useCallback((newTree: NonFinancialDriverTree) => {
    setDraftTree(newTree);
  }, []);

  const handleManualMoveUpdate = useCallback(
    (newTree: NonFinancialDriverTree) => {
      setIsSaving(true);
      updateDriverOrdering(convertTreeToOrder(newTree.tree));
    },
    [updateDriverOrdering]
  );

  const drag = useDriversDragDrop({
    flatData,
    draftTree: draftTree || tree,
    onDragAndDropUpdate: handleDragAndDropUpdate,
    onManualMoveUpdate: handleManualMoveUpdate,
    isUpdating: isUpdating || isSaving
  });

  const startReordering = useCallback(() => {
    const cloned = duplicateTree(tree.index);
    setDraftTree(cloned);
  }, [tree]);

  const cancelReordering = useCallback(() => {
    setDraftTree(undefined);
  }, []);

  const saveReordering = useCallback(async () => {
    if (!draftTree) {
      return;
    }

    setIsSaving(true);
    updateDriverOrdering(convertTreeToOrder(draftTree.tree));
  }, [draftTree, updateDriverOrdering]);

  const value = useMemo(
    () => ({
      isReordering,
      isSaving,
      draftTree,
      flatData,
      drag,
      sourceTree: tree,
      startReordering,
      cancelReordering,
      saveReordering
    }),
    [isReordering, isSaving, draftTree, flatData, drag, tree, startReordering, cancelReordering, saveReordering]
  );

  return <DriverReorderContext.Provider value={value}>{children}</DriverReorderContext.Provider>;
};

export const useDriverReorder = () => {
  const context = useContext(DriverReorderContext);
  if (!context) {
    throw new Error('useDriverReorder must be used within a DriverReorderProvider');
  }
  return context;
};
